// # Reviews page component
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import { db } from '../../auth/firebase';
import { collection, query, where, getDoc, doc, updateDoc } from 'firebase/firestore';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import { formatProductName } from '../../systems/reviews/user_review';
import './review.css';
import reviewsImage from '../../aneo_media/landing/reviews.jpg';
import cacheManager from '../../auth/cache_manager';

function Reviews() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedReviews, setExpandedReviews] = useState(new Set());
  const [selectedReview, setSelectedReview] = useState(null);
  // # Add language state (default to Arabic)
  const [isArabic, setIsArabic] = useState(true);
  // # Add sign in modal state
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }

        // # Dispatch theme change event for footer and other components
        const themeChangeEvent = new CustomEvent('theme_change', {
          detail: { isDark: newTheme }
        });
        window.dispatchEvent(themeChangeEvent);
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Handle success message from location state
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the location state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // # Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // # Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // # In the Reviews component, before the useEffect
  const loadReviews = async () => {
    try {
      const currentYear = new Date().getFullYear().toString();
      const reviewsRef = collection(db, 'reviews');
      const yearDocRef = doc(reviewsRef, currentYear);
      
      // # Try to get current year's reviews
      let yearDoc = await getDoc(yearDocRef);
      let reviewsData = [];
      
      if (yearDoc.exists()) {
        const yearData = yearDoc.data();
        if (yearData.reviews) {
          // # Convert reviews object to array and filter accepted ones
          reviewsData = Object.entries(yearData.reviews)
            .map(([reviewId, review]) => ({
              id: reviewId,
              year: currentYear,
              ...review
            }))
            .filter(review => review.status === 'accepted');
        }
      }
      
      // # If no reviews in current year, try previous year
      if (reviewsData.length === 0) {
        const prevYear = (new Date().getFullYear() - 1).toString();
        const prevYearDocRef = doc(reviewsRef, prevYear);
        const prevYearDoc = await getDoc(prevYearDocRef);
        
        if (prevYearDoc.exists()) {
          const prevYearData = prevYearDoc.data();
          if (prevYearData.reviews) {
            reviewsData = Object.entries(prevYearData.reviews)
              .map(([reviewId, review]) => ({
                id: reviewId,
                year: prevYear,
                ...review
              }))
              .filter(review => review.status === 'accepted');
          }
        }
      }
      
      // # Sort reviews by timestamp, most recent first
      setReviews(reviewsData.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      ));

      // # Set error if no reviews found
      if (reviewsData.length === 0) {
        setError('No reviews available at this moment. Check back later!');
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
      setError('No reviews available at this moment. Check back later or be the first to share your experience!');
    } finally {
      setLoading(false);
    }
  };

  // # Then in useEffect
  useEffect(() => {
    loadReviews();
  }, []);

  // # Static top reviews data with both languages
  const topReviews = [
    {
      name: "Mohammed Elbastawisy",
      nationality: "Egypt 🇪🇬",
      university: "Mansoura University",
      product: "USMLE Step 2 Library",
      contentEn: `The best thing:

- It gathers all the resources, so I don't need to check another source like Amboss
- The notes aren't just like FA for Step 1—they fix FA's issue of being too brief with too few and small images. The radiology images in the library are amazing, especially with the overlay feature
- My favorite part ♥—after every tough topic, Clutch summarizes the key points in a few lines and highlights how to differentiate similar conditions (saved me time + I might not have even noticed those similarities otherwise)
- I love that Step 2 is now like Step 1—I prefer reading before solving, then testing myself to see what I understood well and what I need to work on
- If you go through the notes multiple times and really digest them, scoring 80+ is totally doable.`,
      contentAr: `احلي حاجة:

- انه مجمع المصادر فمش محتاج ابص علي مصدر تاني زي امبوس
- النوتس مش بس زي FA لستب 1 دي تخطيت عيب ان FA مختصر والصورة قليلة فيه وصغيرة , صور الراديولوجي في المكتبة حلوة جدا وخصوصا ان فيها خاصية overlay
- الحاجة اللي بحبها بقي ♥ , بعد كل موضوع رخم بتلاقي Clutch زبدة الموضوع في كام سطر بس وازاي تفرق بين الحاجات المتشابهة (دا وفر عليا وقت + كان ممكن ماخدش بالي ان الحالات دي شبه بعض اصلا )
- فكرة ان ستب 2 بقيت زي ستب 1 كدا انا بحب أقرا قبل ما احل والحل اختبر نفسي بحيث اعرف ان فهمت ايه كويس وايه اللي واقع مني وكدا
- لو قرات النوتس كذا مرة وهضمتها كويس عادي تجيب 80+
- ان في كذا Algorithm فانا بختار اكتر واحد مناسب ليا وحاسه هيفيدني وبعتمد عليه سواء من امبوس او UW او غيره
- المحتوي غني انا بدات بارخم السيتمز بالنسبالي فوق نيورو وكارديو وكدا بس فادتني فعلا في الباطنة حتي لو الدكتور يتناقش معايا في تقييم الامتياز بيلاقيني فاهم الدنيا كويس`
    },
    {
      name: "Omar Sabri",
      nationality: "Egypt 🇪🇬",
      university: "Mansoura University",
      product: "USMLE Step 1 Library",
      contentEn: "You guys have made the best study resource I've ever seen. I've tried many, but nothing combines simplicity and comprehensiveness like this library.",
      contentAr: "انتوا عاملين احسن مصدر مذاكرة شوفته في حياتي، جربت كتير فعلا، مفيش حاجة مبسطة جامعة بين التبسيط و الشمولية زي المكتبة دي."
    },
    {
      name: "Abdulrahman Nabeeh",
      nationality: "Egypt 🇪🇬",
      university: "Suez University",
      product: "USMLE Step 1 Library",
      contentEn: `I'm truly happy and really impressed with the library. I studied from it today, and even though it may seem small, it includes everything I've heard from the videos without missing a single piece of information—Mashallah.

Another thing is that I haven't felt this passionate about studying in a long time. That's because everything is well-organized—the schedule, the videos I need to watch, the timeline I need to follow—I know exactly what I'm doing.

Honestly, hats off to you and the team. The work is top-notch, Mashallah. Today, I was traveling and got back late, so I started studying really late. But even with all that, my mind was structured, I knew exactly what I had to do, and thankfully, I managed to finish everything in the end.

Thank you so much for your effort, you and the whole team!`,
      contentAr: `انا حقيقي مبسوط و مبهور اوي بالمكتبة، ذاكرت منها النهاردة وعلى قد ما هي تبان صغيرة بس فيها كل حاجة انا سمعتها من الفديوهات و مش مفوتة معلومة ما شاء الله  

و حاجة تانية اني من زمان اوي مكنش عندي الشغف ده كله للمذاكرة بسبب ان كل حاجة مترتبة و كل حاجة منظمة، الجدول و الفديوز اللي هشوفها و التايم لاين اللي همشي عليه و عارف راسي من رجلي

فعلاً تسلم ايدك انت و التيم شغل عالي اوي اوي ما شاء الله، انا النهاردة كنت مسافر و رجعت متأخر و بدأت متأخر اوي بس مع كل ده كانت دماغي مترتبة و عارف عليا ايه و الحمد لله قدرت اخلصهم في الاخر

شكراً على مجهودك انت و التيم والله`
    },
    {
      name: "Nitish Naidu",
      nationality: "India 🇮🇳",
      product: "USMLE Step 2 Library",
      contentEn: "Using the library during my cardiology elective was a game changer. It's so concise and high yield that I was able to answer most of my attending's questions. There were several instances where in I was quizzed on topics that were literally highlighted in the library making it a top resource for me. It made me shine during my rotation."
    }
  ];

  const handleWriteReview = () => {
    if (!isAuthenticated) {
      setShowSignInModal(true);
      return;
    }

    // # Check if we're in admin path
    const isAdminPath = location.pathname.startsWith('/admin');
    const writeReviewPath = isAdminPath ? '/admin/write-review' : '/write-review';
    
    // # Navigate to appropriate write review path
    navigate(writeReviewPath);
  };

  const toggleReviewExpansion = (reviewId) => {
    setExpandedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  const handleReadMore = (review) => {
    setSelectedReview(review);
  };

  // # Update ReviewModal component
  const ReviewModal = ({ review, onClose }) => (
    <div className="mn_rvw_review-modal-overlay" onClick={onClose}>
      <div className="mn_rvw_review-modal" onClick={e => e.stopPropagation()}>
        <button className="mn_rvw_modal-close" onClick={onClose}>×</button>
        <div className="mn_rvw_modal-content">
          <div className="mn_rvw_modal-header">
            <h2 className="mn_rvw_modal-title">{review.name || review.userName}</h2>
            {review.nationality && (
              <p className="mn_rvw_modal-nationality">{review.nationality}</p>
            )}
            {review.university && (
              <p className="mn_rvw_modal-university">{review.university}</p>
            )}
            <p className="mn_rvw_modal-product">{review.product}</p>
            {review.timestamp && (
              <span className="mn_rvw_modal-date">
                {new Date(review.timestamp).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="mn_rvw_modal-body">
            <p className="mn_rvw_modal-text">{review.content}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // # Add language toggle function with animation
  const toggleLanguage = () => {
    // Get all review text elements
    const reviewTexts = document.querySelectorAll('.mn_rvw_review-text');
    
    // Add fade-out class to trigger animation
    reviewTexts.forEach(text => {
      text.classList.add('fade-out');
    });

    // Wait for fade-out animation to complete before changing language
    setTimeout(() => {
      setIsArabic(!isArabic);
      
      // Add fade-in class after content change
      setTimeout(() => {
        reviewTexts.forEach(text => {
          text.classList.remove('fade-out');
          text.classList.add('fade-in');
          
          // Remove fade-in class after animation completes
          setTimeout(() => {
            text.classList.remove('fade-in');
          }, 300);
        });
      }, 50);
    }, 300);
  };

  // # Format content function with animation support
  const formatContent = (content) => {
    if (!content) return ''; // Add check for undefined content
    
    return content
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      .replace(/♥/g, '<span data-special="heart">♥</span>');
  };

  // # Add SignInModal component
  const SignInModal = ({ onClose }) => {
    const handleSignIn = () => {
      navigate('/signin', { 
        state: { 
          from: '/write-review'
        } 
      });
      onClose();
    };

    return (
      <div 
        className={`mn_rvw_signin-modal-overlay ${showSignInModal ? 'active' : ''}`}
        onClick={onClose}
      >
        <div 
          className={`mn_rvw_signin-modal ${showSignInModal ? 'active' : ''}`}
          onClick={e => e.stopPropagation()}
        >
          <button className="mn_rvw_signin-modal-close" onClick={onClose}>×</button>
          <div className="mn_rvw_signin-modal-content">
            <div className="mn_rvw_signin-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
              </svg>
            </div>
            <h2 className="mn_rvw_signin-title">Sign In Required</h2>
            <p className="mn_rvw_signin-message">
              Please sign in to share your experience and help others make informed decisions.
            </p>
            <div className="mn_rvw_signin-buttons">
              <button 
                className="mn_rvw_signin-button primary"
                onClick={handleSignIn}
              >
                Sign In
              </button>
              <button 
                className="mn_rvw_signin-button secondary"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`mn_rvw_reviews-page ${isDarkMode ? 'mn_rvw_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      {/* # Hero Section */}
      <section className="mn_rvw_hero-section">
        <div 
          className="mn_rvw_hero-background"
          style={{ backgroundImage: `url(${reviewsImage})` }}
        ></div>
        <div className="mn_rvw_hero-content">
          <h1 className="mn_rvw_hero-title">Student Success Stories</h1>
          <p className="mn_rvw_hero-subtitle">
            Discover how TEMP-U has helped medical students achieve their USMLE goals
          </p>
        </div>
      </section>
      
      {/* # Success Message */}
      {successMessage && (
        <div className="mn_rvw_success-message">
          {successMessage}
        </div>
      )}
      
      {/* # Top Reviews Section */}
      <section className="mn_rvw_top-reviews-section">
        <div className="mn_rvw_section-content">
          <h2 className="mn_rvw_section-title">Top Reviews</h2>
          <p className="mn_rvw_section-subtitle">Success Stories from Our Top Performers</p>
          
          <div className="mn_rvw_reviews-grid">
            {topReviews.map((review, index) => (
              <div key={index} className={`mn_rvw_review-card ${isArabic && review.contentAr ? 'mn_rvw_arabic' : ''}`}>
                <div className="mn_rvw_reviewer-info">
                  <div className="mn_rvw_reviewer-header">
                    <div className="mn_rvw_reviewer-details">
                      <h3 className="mn_rvw_reviewer-name">{review.name}</h3>
                      {review.nationality && (
                        <p className="mn_rvw_reviewer-nationality">{review.nationality}</p>
                      )}
                      {review.university && (
                        <p className="mn_rvw_reviewer-university">{review.university}</p>
                      )}
                      <p className="mn_rvw_reviewer-product">{review.product}</p>
                    </div>
                    {review.contentAr && (
                      <div className="mn_rvw_language-toggle">
                        <button 
                          className={`mn_rvw_toggle-button ${isArabic ? 'mn_rvw_arabic-active' : ''}`}
                          onClick={toggleLanguage}
                          aria-label="Toggle language"
                        >
                          <span className={`mn_rvw_toggle-text ${!isArabic ? 'active' : ''}`}>A</span>
                          <span className={`mn_rvw_toggle-text ${isArabic ? 'active' : ''}`}>أ</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mn_rvw_review-content">
                  <p 
                    className="mn_rvw_review-text"
                    dangerouslySetInnerHTML={{
                      __html: formatContent(isArabic && review.contentAr ? review.contentAr : review.contentEn)
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* # Write Review Button */}
      <div className="mn_rvw_write-review-section">
        <button 
          className="mn_rvw_write-review-button"
          onClick={handleWriteReview}
        >
          Write a Review
        </button>
      </div>

      {/* # User Reviews Section */}
      <section className="mn_rvw_more-reviews-section">
        <div className="mn_rvw_section-content">
          <h2 className="mn_rvw_section-title">More Reviews</h2>
          <p className="mn_rvw_section-subtitle">Hear from Our Community</p>
          
          {loading ? (
            <div className="mn_rvw_loading">
              <div className="mn_rvw_loading-spinner"></div>
              <p>Loading reviews...</p>
            </div>
          ) : error ? (
            <div className="mn_rvw_error">
              <div className="mn_rvw_error-icon">!</div>
              <h3 className="mn_rvw_error-title">Oops! Something went wrong</h3>
              <p className="mn_rvw_error-message">{error}</p>
              <button 
                className="mn_rvw_retry-button"
                onClick={() => {
                  setLoading(true);
                  setError('');
                  loadReviews();
                }}
              >
                <span className="mn_rvw_retry-icon">↻</span>
                Try Loading Again
              </button>
            </div>
          ) : reviews.length > 0 ? (
            <div className="mn_rvw_reviews-grid">
              {reviews.map((review) => (
                <div key={review.id} className="mn_rvw_review-card">
                  <div className="mn_rvw_reviewer-info">
                    <h3 className="mn_rvw_reviewer-name">{review.userName}</h3>
                    <p className="mn_rvw_reviewer-product">
                      {formatProductName(review.product)}
                    </p>
                    <span className="mn_rvw_review-date">
                      {new Date(review.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="mn_rvw_review-content">
                    <p className="mn_rvw_review-text">
                      {review.content.length > 200 
                        ? `${review.content.substring(0, 200)}...` 
                        : review.content
                      }
                    </p>
                    <div className="mn_rvw_read-more-container">
                      <button 
                        className="mn_rvw_read-button"
                        onClick={() => handleReadMore(review)}
                      >
                        Read
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mn_rvw_review-card mn_rvw_placeholder">
              <p className="mn_rvw_coming-soon">Be the first to share your experience!</p>
              <p className="mn_rvw_sub-text">Your review helps others make informed decisions</p>
              <button 
                className="mn_rvw_write-review-button-alt"
                onClick={handleWriteReview}
              >
                Write a Review
              </button>
            </div>
          )}
        </div>
      </section>

      {/* # Review Modal */}
      {selectedReview && (
        <ReviewModal
          review={selectedReview}
          onClose={() => setSelectedReview(null)}
        />
      )}

      {/* # Add SignInModal */}
      <SignInModal onClose={() => setShowSignInModal(false)} />

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default Reviews;
